import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { normLang } from "@/i18n";
import api, { apiError, money } from "@/lib/api";
import { toast } from "sonner";
import { Banknote, Settings2, RefreshCw, Check, Search as SearchIcon, X, AlertTriangle, FileText, History } from "lucide-react";
import { WithdrawalReceipt } from "@/components/shared/WithdrawalReceipt";

const TXT = {
  fr: {
    title: "Retraits partenaires", refresh: "Actualiser", settings: "Paramètres",
    unavailable: "Non disponible", available: "Disponible", sent: "Envoyé", failResend: "Échec — Renvoyer",
    receiptHistory: "Historique du reçu", loading: "Chargement…", noEvent: "Aucun événement.",
    error: "Erreur :", admin: "Admin :",
    settingsTitle: "Paramètres des retraits", minWithdraw: "Retrait minimum (USD)",
    dailyCapGross: "Plafond quotidien brut (USD)", feePct: "Frais de retrait (%)",
    holdDays: "Hold des gains (jours)", procTime: "Délai de traitement indicatif", save: "Enregistrer",
    programTitle: "Programme Partenaire", commissionRate: "Commission de parrainage (%)", commissionHold: "Blocage des commissions (jours)",
    settingsSaved: "Paramètres enregistrés",
    stCount: "Demandes", stRequested: "Demandé", stPending: "En attente", stApproved: "Approuvé",
    stPaid: "Payé", stFees: "Frais générés", stAttention: "À traiter",
    allStatuses: "Tous statuts", emailAll: "E-mail reçu : tous", emailSent: "E-mail envoyé", emailFailed: "E-mail en échec",
    resendAll: "Renvoi : tous", withResend: "Avec renvoi", withoutResend: "Sans renvoi",
    member: "Membre", nameEmail: "Nom / E-mail", requested: "Demandé", fee: "Frais", net: "Net",
    method: "Méthode", status: "Statut", receiptEmail: "Reçu / E-mail", lastSent: "Dernier envoi",
    resends: "Renvois", risk: "Risque", actions: "Actions", noRequest: "Aucune demande.",
    receipt: "Reçu", journal: "Journal", resend: "Renvoyer", validate: "Valider", review: "À étudier",
    reject: "Refuser", process: "Traiter", paid: "Payé", fail: "Échec",
    promptReject: "Motif du refus :", promptComment: "Commentaire :", promptRef: "Référence de paiement (recommandée) :",
    updated: "Mis à jour", receiptResent: "Reçu renvoyé par e-mail",
  },
  en: {
    title: "Partner withdrawals", refresh: "Refresh", settings: "Settings",
    unavailable: "Unavailable", available: "Available", sent: "Sent", failResend: "Failed — Resend",
    receiptHistory: "Receipt history", loading: "Loading…", noEvent: "No event.",
    error: "Error:", admin: "Admin:",
    settingsTitle: "Withdrawal settings", minWithdraw: "Minimum withdrawal (USD)",
    dailyCapGross: "Daily gross limit (USD)", feePct: "Withdrawal fee (%)",
    holdDays: "Earnings hold (days)", procTime: "Indicative processing time", save: "Save",
    programTitle: "Partner program", commissionRate: "Referral commission (%)", commissionHold: "Commission hold (days)",
    settingsSaved: "Settings saved",
    stCount: "Requests", stRequested: "Requested", stPending: "Pending", stApproved: "Approved",
    stPaid: "Paid", stFees: "Fees generated", stAttention: "To handle",
    allStatuses: "All statuses", emailAll: "Receipt email: all", emailSent: "Email sent", emailFailed: "Email failed",
    resendAll: "Resend: all", withResend: "With resend", withoutResend: "Without resend",
    member: "Member", nameEmail: "Name / Email", requested: "Requested", fee: "Fee", net: "Net",
    method: "Method", status: "Status", receiptEmail: "Receipt / Email", lastSent: "Last sent",
    resends: "Resends", risk: "Risk", actions: "Actions", noRequest: "No request.",
    receipt: "Receipt", journal: "Journal", resend: "Resend", validate: "Approve", review: "Review",
    reject: "Reject", process: "Process", paid: "Paid", fail: "Fail",
    promptReject: "Rejection reason:", promptComment: "Comment:", promptRef: "Payment reference (recommended):",
    updated: "Updated", receiptResent: "Receipt resent by email",
  },
};
const useT = () => { const { i18n } = useTranslation(); return TXT[normLang(i18n.language)] || TXT.fr; };

const EVT_STYLE = {
  SUCCESS: "text-emerald-500",
  FAILED: "text-rose-500",
};

function ReceiptCell({ w, L }) {
  if (!w.receipt_available) return <span className="text-xs text-muted-foreground">{L.unavailable}</span>;
  const es = w.receipt_email_status;
  return (
    <div className="text-xs">
      <div className="text-emerald-500">{L.available}</div>
      {es === "sent" && <div className="text-emerald-500">{L.sent}</div>}
      {es === "failed" && <div className="font-semibold text-rose-500">{L.failResend}</div>}
      {!es && <div className="text-muted-foreground">—</div>}
    </div>
  );
}

function JournalModal({ wid, onClose, L }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/admin/partner/withdrawals/${wid}/receipt-journal`).then((r) => setRows(r.data.items)).catch(() => setRows([])); }, [wid]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-2xl border border-border bg-card p-5" data-testid="receipt-journal-modal">
        <div className="mb-3 flex items-center justify-between"><h3 className="flex items-center gap-2 font-bold"><History className="h-5 w-5" /> {L.receiptHistory}</h3><button onClick={onClose}><X className="h-5 w-5" /></button></div>
        {!rows ? <p className="text-sm text-muted-foreground">{L.loading}</p> :
          !rows.length ? <p className="text-sm text-muted-foreground">{L.noEvent}</p> :
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={i} className="flex items-start justify-between rounded-lg border border-border p-2 text-sm" data-testid={`journal-evt-${r.event}`}>
                <div>
                  <div className="font-medium">{r.event_label}</div>
                  {r.error_message && <div className="text-xs text-rose-500">{L.error} {r.error_message}</div>}
                  {r.admin_id && <div className="text-[11px] text-muted-foreground">{L.admin} {r.admin_id}</div>}
                  {r.email && <div className="text-[11px] text-muted-foreground">{r.email}</div>}
                </div>
                <div className="text-right">
                  <span className={`text-xs font-semibold ${EVT_STYLE[r.status] || ""}`}>{r.status}</span>
                  <div className="text-[11px] text-muted-foreground">{(r.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
              </div>
            ))}
          </div>}
      </div>
    </div>
  );
}

const STATUS_COLORS = {
  REQUESTED: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  UNDER_REVIEW: "bg-amber-500/10 text-amber-500 border-amber-500/30",
  APPROVED: "bg-sky-500/10 text-sky-500 border-sky-500/30",
  PROCESSING: "bg-indigo-500/10 text-indigo-500 border-indigo-500/30",
  PAID: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
  FAILED: "bg-rose-500/10 text-rose-500 border-rose-500/30",
  REJECTED: "bg-rose-500/10 text-rose-500 border-rose-500/30",
  CANCELLED: "bg-slate-500/10 text-slate-500 border-slate-500/30",
};

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-display text-lg font-bold">{value}</div>
    </div>
  );
}

function SettingsPanel({ onClose, onSaved, L }) {
  const [s, setS] = useState(null);
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/admin/partner/withdrawals/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/partner/program-settings").then((r) => setP(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const save = async () => {
    try {
      if (p) {
        await api.put("/admin/partner/program-settings", {
          default_commission_rate: parseFloat(p.default_commission_rate),
          commission_hold_days: parseInt(p.commission_hold_days, 10),
        });
      }
      await api.put("/admin/partner/withdrawals/settings", {
        min_withdrawal_usd: parseFloat(s.min_withdrawal_usd),
        max_daily_withdrawal_usd: parseFloat(s.max_daily_withdrawal_usd),
        withdrawal_fee_rate: parseFloat(s.withdrawal_fee_pct ?? s.withdrawal_fee_rate * 100),
        hold_days: parseInt(s.hold_days, 10), processing_time: s.processing_time, note: "admin update",
      });
      toast.success(L.settingsSaved); onSaved?.(); onClose();
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-xl border border-border bg-card p-5" data-testid="wd-settings-modal">
        <div className="mb-3 flex items-center justify-between"><h3 className="font-bold">{L.settingsTitle}</h3><button onClick={onClose}><X className="h-5 w-5" /></button></div>
        <div className="space-y-3 text-sm">
          <div><label className="text-xs text-muted-foreground">{L.minWithdraw}</label>
            <input data-testid="set-min" type="number" value={s.min_withdrawal_usd} onChange={(e) => setS({ ...s, min_withdrawal_usd: e.target.value })} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2" /></div>
          <div><label className="text-xs text-muted-foreground">{L.dailyCapGross}</label>
            <input data-testid="set-max-daily" type="number" value={s.max_daily_withdrawal_usd} onChange={(e) => setS({ ...s, max_daily_withdrawal_usd: e.target.value })} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2" /></div>
          <div><label className="text-xs text-muted-foreground">{L.feePct}</label>
            <input data-testid="set-fee" type="number" value={s.withdrawal_fee_pct} onChange={(e) => setS({ ...s, withdrawal_fee_pct: e.target.value })} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2" /></div>
          <div><label className="text-xs text-muted-foreground">{L.holdDays}</label>
            <input data-testid="set-hold" type="number" value={s.hold_days} onChange={(e) => setS({ ...s, hold_days: e.target.value })} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2" /></div>
          <div><label className="text-xs text-muted-foreground">{L.procTime}</label>
            <input data-testid="set-proc" value={s.processing_time} onChange={(e) => setS({ ...s, processing_time: e.target.value })} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2" /></div>
          {p && (
            <div className="border-t border-border pt-3" data-testid="program-settings">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide">{L.programTitle}</div>
              <label className="text-xs text-muted-foreground">{L.commissionRate}</label>
              <input data-testid="set-commission-rate" type="number" value={p.default_commission_rate} onChange={(e) => setP({ ...p, default_commission_rate: e.target.value })} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2" />
              <label className="mt-3 block text-xs text-muted-foreground">{L.commissionHold}</label>
              <input data-testid="set-commission-hold" type="number" value={p.commission_hold_days} onChange={(e) => setP({ ...p, commission_hold_days: e.target.value })} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2" />
            </div>
          )}
        </div>
        <button onClick={save} data-testid="set-save" className="mt-4 w-full rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white">{L.save}</button>
      </div>
    </div>
  );
}

export default function AdminPartnerWithdrawals() {
  const L = useT();
  const [data, setData] = useState({ items: [], stats: {} });
  const [status, setStatus] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [busy, setBusy] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [journalId, setJournalId] = useState(null);
  const [searchParams] = useSearchParams();
  const [receiptEmail, setReceiptEmail] = useState(
    searchParams.get("receipt_email") === "failed" ? "failed" : ""
  );
  const [hasResend, setHasResend] = useState("");

  const viewReceipt = async (wid) => {
    try { const r = await api.get(`/admin/partner/withdrawals/${wid}/receipt`); setReceipt(r.data); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  };

  const load = useCallback(async () => {
    try {
      const params = {};
      if (status) params.status = status;
      if (receiptEmail) params.receipt_email = receiptEmail;
      if (hasResend) params.has_resend = hasResend;
      const r = await api.get("/admin/partner/withdrawals", { params });
      setData(r.data);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  }, [status, receiptEmail, hasResend]);
  useEffect(() => { load(); }, [load]);

  const act = async (wid, action, needNote) => {
    let note = "", ref = "";
    if (needNote) { note = window.prompt(action === "reject" ? L.promptReject : L.promptComment) || ""; if (!note.trim()) return; }
    if (action === "mark-paid") { ref = window.prompt(L.promptRef) || ""; }
    setBusy(wid);
    try {
      await api.post(`/admin/partner/withdrawals/${wid}/${action}`, { note, payment_reference: ref });
      toast.success(L.updated); await load();
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    setBusy(null);
  };

  const resendReceipt = async (wid) => {
    setBusy(wid);
    try { await api.post(`/admin/partner/withdrawals/${wid}/resend-receipt`); toast.success(L.receiptResent); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    setBusy(null);
  };

  const st = data.stats || {};
  return (
    <div className="space-y-5" data-testid="admin-partner-withdrawals">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 font-display text-xl font-bold"><Banknote className="h-5 w-5 text-accent" /> {L.title}</h1>
        <div className="flex gap-2">
          <button onClick={load} data-testid="wd-refresh" className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-secondary"><RefreshCw className="h-4 w-4" /> {L.refresh}</button>
          <button onClick={() => setShowSettings(true)} data-testid="wd-settings-btn" className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-secondary"><Settings2 className="h-4 w-4" /> {L.settings}</button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
        <Stat label={L.stCount} value={st.count || 0} />
        <Stat label={L.stRequested} value={money(st.total_requested)} />
        <Stat label={L.stPending} value={money(st.pending)} />
        <Stat label={L.stApproved} value={money(st.approved)} />
        <Stat label={L.stPaid} value={money(st.paid)} />
        <Stat label={L.stFees} value={money(st.total_fees)} />
        <Stat label={L.stAttention} value={st.needs_attention || 0} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="wd-filter-status" className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm">
          <option value="">{L.allStatuses}</option>
          {["pending", "under_review", "approved", "processing", "paid", "failed", "rejected", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={receiptEmail} onChange={(e) => setReceiptEmail(e.target.value)} data-testid="wd-filter-email" className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm">
          <option value="">{L.emailAll}</option>
          <option value="sent">{L.emailSent}</option>
          <option value="failed">{L.emailFailed}</option>
        </select>
        <select value={hasResend} onChange={(e) => setHasResend(e.target.value)} data-testid="wd-filter-resend" className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm">
          <option value="">{L.resendAll}</option>
          <option value="yes">{L.withResend}</option>
          <option value="no">{L.withoutResend}</option>
        </select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs text-muted-foreground"><tr>
            <th className="px-2 py-2 text-left">{L.member}</th><th className="px-2 py-2 text-left">{L.nameEmail}</th>
            <th className="px-2 py-2 text-right">{L.requested}</th>
            <th className="px-2 py-2 text-right">{L.fee}</th><th className="px-2 py-2 text-right">{L.net}</th>
            <th className="px-2 py-2 text-left">{L.method}</th><th className="px-2 py-2 text-left">{L.status}</th>
            <th className="px-2 py-2 text-left">{L.receiptEmail}</th><th className="px-2 py-2 text-left">{L.lastSent}</th><th className="px-2 py-2 text-center">{L.resends}</th>
            <th className="px-2 py-2 text-left">{L.risk}</th><th className="px-2 py-2 text-right">{L.actions}</th>
          </tr></thead>
          <tbody>
            {data.items.map((w) => (
              <tr key={w.id} className="border-t border-border" data-testid={`wd-row-${w.id}`}>
                <td className="px-2 py-2 font-mono text-xs">{w.member_code || "—"}<div className="text-muted-foreground">{w.reference}</div></td>
                <td className="px-2 py-2 text-xs">{w.full_name || "—"}<div className="text-muted-foreground">{w.email || "—"}</div></td>
                <td className="px-2 py-2 text-right">{money(w.requested_amount ?? w.amount)}</td>
                <td className="px-2 py-2 text-right text-rose-500">{money(w.withdrawal_fee)}</td>
                <td className="px-2 py-2 text-right text-emerald-500">{money(w.net_payout)}</td>
                <td className="px-2 py-2 text-xs">{w.method === "usdt" ? `USDT ${w.usdt_network || ""}` : `MM ${w.mm_country || ""}`}<div className="text-muted-foreground">{w.method === "usdt" ? "" : (w.mm_operator || "")} {w.coordinates_masked}</div></td>
                <td className="px-2 py-2"><span className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLORS[w.status_label] || ""}`}>{w.status_label}</span></td>
                <td className="px-2 py-2"><ReceiptCell w={w} L={L} /></td>
                <td className="px-2 py-2 text-xs text-muted-foreground">{w.receipt_last_sent_at ? (w.receipt_last_sent_at || "").slice(0, 16).replace("T", " ") : "—"}</td>
                <td className="px-2 py-2 text-center text-xs">{w.receipt_resend_count || 0}</td>
                <td className="px-2 py-2 text-xs">{w.risk_state !== "OK" ? <span className="inline-flex items-center gap-1 text-amber-500"><AlertTriangle className="h-3.5 w-3.5" />{w.risk_state}</span> : "OK"}</td>
                <td className="px-2 py-2 text-right">
                  <div className="flex flex-wrap justify-end gap-1">
                    {w.status === "paid" && <button onClick={() => viewReceipt(w.id)} data-testid={`wd-receipt-${w.id}`} className="rounded-md border border-border px-2 py-1 text-[11px] hover:bg-secondary"><FileText className="inline h-3 w-3" /> {L.receipt}</button>}
                    {w.status === "paid" && <button onClick={() => setJournalId(w.id)} data-testid={`wd-journal-${w.id}`} className="rounded-md border border-border px-2 py-1 text-[11px] hover:bg-secondary"><History className="inline h-3 w-3" /> {L.journal}</button>}
                    {w.status === "paid" && <button onClick={() => resendReceipt(w.id)} disabled={busy === w.id} data-testid={`wd-resend-${w.id}`} className="rounded-md border border-border px-2 py-1 text-[11px] hover:bg-secondary">{L.resend}</button>}
                    {["pending", "under_review"].includes(w.status) && <button onClick={() => act(w.id, "approve")} disabled={busy === w.id} data-testid={`wd-approve-${w.id}`} className="rounded-md bg-sky-600 px-2 py-1 text-[11px] text-white">{L.validate}</button>}
                    {["pending", "under_review", "approved"].includes(w.status) && <button onClick={() => act(w.id, "review", true)} className="rounded-md bg-amber-600 px-2 py-1 text-[11px] text-white">{L.review}</button>}
                    {["pending", "under_review", "approved", "processing"].includes(w.status) && <button onClick={() => act(w.id, "reject", true)} data-testid={`wd-reject-${w.id}`} className="rounded-md bg-rose-600 px-2 py-1 text-[11px] text-white">{L.reject}</button>}
                    {w.status === "approved" && <button onClick={() => act(w.id, "processing")} className="rounded-md bg-indigo-600 px-2 py-1 text-[11px] text-white">{L.process}</button>}
                    {["approved", "processing"].includes(w.status) && <button onClick={() => act(w.id, "mark-paid")} data-testid={`wd-paid-${w.id}`} className="rounded-md bg-emerald-600 px-2 py-1 text-[11px] text-white"><Check className="inline h-3 w-3" /> {L.paid}</button>}
                    {["approved", "processing"].includes(w.status) && <button onClick={() => act(w.id, "mark-failed", true)} className="rounded-md bg-rose-700 px-2 py-1 text-[11px] text-white">{L.fail}</button>}
                  </div>
                </td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan={12} className="px-2 py-6 text-center text-muted-foreground">{L.noRequest}</td></tr>}
          </tbody>
        </table>
      </div>

      {showSettings && <SettingsPanel onClose={() => setShowSettings(false)} onSaved={load} L={L} />}
      {receipt && <WithdrawalReceipt receipt={receipt} onClose={() => setReceipt(null)} />}
      {journalId && <JournalModal wid={journalId} onClose={() => setJournalId(null)} L={L} />}
    </div>
  );
}
